import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import AdBanner from '../components/AdBanner';
import PanchangBanner from '../components/panchang/PanchangBanner';
import DateSelector from '../components/panchang/DateSelector';
import PanchangDetails from '../components/panchang/PanchangDetails';
import MonthlyGrid from '../components/panchang/MonthlyGrid';
import PanchangContent from '../components/panchang/PanchangContent';
import PanchangFAQ, { PANCHANG_FAQS } from '../components/panchang/PanchangFAQ';
import { getPanchangData } from '../services/panchangApi';
import { generateBreadcrumbSchema, generateWebPageSchema, generateFAQSchema } from '../utils/schemaGenerator';
import { RefreshCw, AlertCircle } from 'lucide-react';

const Panchang2026 = () => {
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    // Default to today if it is 2026, otherwise default to May 23, 2026
    if (today.getFullYear() === 2026) {
      return today;
    }
    return new Date(2026, 4, 23); // May 23, 2026
  });
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPanchang = async (date) => {
    setLoading(true);
    setError(null);
    try {
      // Date string formatted for API caching/request keying
      const dateStr = date.toISOString().split('T')[0];
      const result = await getPanchangData(dateStr);
      setData(result);
    } catch (err) {
      console.error("Error fetching panchang:", err);
      setError("पंचांग डेटा लोड करने में असमर्थ। कृपया पुनः प्रयास करें। (Failed to fetch Panchang data)");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPanchang(selectedDate);
  }, [selectedDate]);

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const handleRetry = () => {
    fetchPanchang(selectedDate);
  };

  // Dynamic SEO values
  const dateObj = new Date(selectedDate);
  const formattedDateStr = dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
  
  let dynamicTitle = `Panchang 2026 - Daily Hindu Calendar | पंचांग 2026`;
  let dynamicDesc = `Explore the accurate Hindu calendar (Panchang) for 2026 with daily Tithi, Nakshatra, Yoga, Karana, and Sunrise/Sunset times for Bareilly, Uttar Pradesh.`;
  let dynamicKeywords = `panchang 2026, hindu calendar 2026, daily tithi, bareilly panchang, nakshatra 2026, yoga karana, sunrise bareilly, nathnagri calendar, today panchang, hindi panchang today, daily panchang online, hindu tithi today, today muhurat`;

  if (data) {
    const tithiNameEn = data.tithi.name.en;
    const nakshatraNameEn = data.nakshatra.name.en;
    const monthNameEn = data.hinduMonth.en;
    
    dynamicTitle = `Today Panchang ${formattedDateStr} - ${tithiNameEn}, ${nakshatraNameEn} | Hindu Calendar`;
    dynamicDesc = `Check Today Panchang for ${formattedDateStr}. Today's Tithi is ${tithiNameEn} in ${monthNameEn} month. Moon is in ${nakshatraNameEn} Nakshatra. View precise Shubh Muhurat, Sunrise, and Sunset timings for Bareilly.`;
    dynamicKeywords += `, ${tithiNameEn.toLowerCase()} today, nakshatra ${nakshatraNameEn.toLowerCase()}, ${monthNameEn.toLowerCase()} panchang`;
  }

  // SEO Schemas
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://nathnagribareilly.com/panchang-2026';
  
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Panchang 2026', url: '/panchang-2026' }
  ]);

  const webPageSchema = generateWebPageSchema('WebPage', {
    name: dynamicTitle,
    description: dynamicDesc
  }, currentUrl);
  
  const faqSchema = generateFAQSchema(PANCHANG_FAQS);

  const rawSchemas = [breadcrumbSchema, webPageSchema, faqSchema].filter(Boolean);

  return (
    <div className="min-h-screen bg-stone-50 py-10 md:py-16">
      <SEO
        title={dynamicTitle}
        description={dynamicDesc}
        keywords={dynamicKeywords}
        url={currentUrl}
        schema={rawSchemas}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* BANNER SECTION */}
        <PanchangBanner />

        {/* DATE SELECTOR MODULE */}
        <DateSelector 
          selectedDate={selectedDate} 
          onDateChange={handleDateChange} 
          panchangData={data}
        />

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content (Left) */}
          <div className="flex-1 space-y-8 min-h-[400px]">
            {/* MONTHLY CALENDAR GRID WIDGET */}
            <MonthlyGrid 
              selectedDate={selectedDate} 
              onDateChange={handleDateChange} 
            />
            {loading ? (
              /* Loading Spinner Card */
              <div className="bg-white rounded-3xl border border-stone-150 p-12 shadow-sm flex flex-col justify-center items-center h-full min-h-[350px]">
                <div className="relative">
                  <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-saffron-600"></div>
                  <div className="absolute inset-0 m-auto h-6 w-6 bg-stone-100 rounded-full flex items-center justify-center">
                    <span className="text-saffron-600 font-bold text-xs">🕉️</span>
                  </div>
                </div>
                <p className="mt-6 text-stone-600 font-bold text-lg animate-pulse tracking-wide">
                  वैदिक पंचांग की गणना की जा रही है...
                </p>
                <p className="mt-1.5 text-stone-400 text-sm">
                  Calculating Hindu Calendar variables...
                </p>
              </div>
            ) : error ? (
              /* Error & Retry State */
              <div className="bg-white rounded-3xl border border-stone-150 p-10 shadow-sm flex flex-col justify-center items-center text-center h-full min-h-[350px]">
                <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
                <h4 className="text-xl font-bold text-stone-900 mb-2">गणना में त्रुटि (Calculation Error)</h4>
                <p className="text-stone-600 max-w-md mb-6">{error}</p>
                <button
                  onClick={handleRetry}
                  className="px-6 py-3 bg-saffron-500 hover:bg-saffron-600 text-stone-900 font-bold rounded-xl shadow-lg transition-all flex items-center space-x-2"
                >
                  <RefreshCw className="h-5 w-5" />
                  <span>पुनः प्रयास करें (Retry)</span>
                </button>
              </div>
            ) : !data ? (
              /* Empty State Fallback */
              <div className="bg-white rounded-3xl border border-stone-150 p-12 shadow-sm flex flex-col justify-center items-center text-center h-full min-h-[350px]">
                <AlertCircle className="h-12 w-12 text-stone-300 mb-3" />
                <h4 className="text-lg font-bold text-stone-850">कोई डेटा उपलब्ध नहीं</h4>
                <p className="text-stone-500 text-sm mt-1">
                  इस तिथि के लिए कोई वैदिक डेटा उपलब्ध नहीं है। कृपया भिन्न तिथि चुनें।
                </p>
              </div>
            ) : (
              /* Panchang Elements & Timings Display */
              <PanchangDetails data={data} />
            )}
          </div>

          {/* Sidebar Section (Right) */}
          <div className="w-full lg:w-80 flex-shrink-0 space-y-6">
            <div className="bg-white border border-stone-150 rounded-3xl p-6 shadow-sm">
              <h4 className="font-extrabold text-stone-900 text-lg mb-3 pb-2 border-b border-stone-100 flex items-center space-x-2">
                <span>🗓️ वैदिक महत्व</span>
              </h4>
              <p className="text-xs text-stone-600 leading-relaxed">
                वैदिक संस्कृति में पंचांग का विशेष स्थान है। "पंचांग" का अर्थ है पांच अंग - <strong>तिथि, नक्षत्र, योग, करण और वार</strong>। शुभ मुहूर्त के चयन, पूजा-पाठ, और साधना के लिए प्रतिदिन पंचांग देखना अत्यंत कल्याणकारी माना जाता है।
              </p>
            </div>

            <AdBanner placement="sidebar" pageName="global" />
          </div>
        </div>

        {/* SEO CONTENT SECTION */}
        <PanchangContent />

        {/* FAQ SECTION */}
        <PanchangFAQ />
      </div>
    </div>
  );
};

export default Panchang2026;
